import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface AuroraLoadingOverlayProps {
  isLoading: boolean;
  message?: string;
}

const CYCLING_MESSAGES = [
  'Consultando as forças cósmicas...',
  'Aurora está meditando...',
  'Tecendo os fios do destino...',
  'As energias convergindo...',
  'Alinhando os astros...',
  'Interpretando os sinais...',
];

const AuroraLoadingOverlay: React.FC<AuroraLoadingOverlayProps> = ({
  isLoading,
  message = 'Aurora está falando...',
}) => {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    if (isLoading) {
      setProgress(0);
      setMsgIndex(0);
      setVisible(true);
    } else if (visible) {
      setProgress(100);
      const t = setTimeout(() => setVisible(false), 500);
      return () => clearTimeout(t);
    }
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoading || !visible) return;
    // Curva desacelerada, mas nunca totalmente parada: mesmo em esperas longas o
    // número sempre sobe um pouco (mesmo que devagar), pra não parecer travado.
    // Só chega perto de 99% se a espera real for bem mais longa que o normal.
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 96) return Math.min(prev + 0.015, 99);
        if (prev >= 85) return prev + 0.08;
        if (prev >= 60) return prev + 0.35;
        return prev + 1.6;
      });
    }, 120);
    return () => clearInterval(interval);
  }, [isLoading, visible]);

  useEffect(() => {
    if (!isLoading) return;
    const t = setInterval(() => setMsgIndex(i => (i + 1) % CYCLING_MESSAGES.length), 3500);
    return () => clearInterval(t);
  }, [isLoading]);

  const radius = 48;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress / 100);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.88, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.88, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col items-center gap-5 rounded-2xl border border-white/20 bg-white/10 px-10 py-8 backdrop-blur-sm text-center max-w-xs w-full mx-4"
          >
            <div className="relative h-28 w-28">
              <svg className="h-28 w-28 -rotate-90" viewBox="0 0 112 112">
                <circle
                  cx="56" cy="56" r={radius}
                  fill="none"
                  stroke="rgba(255,255,255,0.12)"
                  strokeWidth="7"
                />
                <circle
                  cx="56" cy="56" r={radius}
                  fill="none"
                  stroke="url(#aurora-prog)"
                  strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  style={{ transition: 'stroke-dashoffset 0.25s ease' }}
                />
                <defs>
                  <linearGradient id="aurora-prog" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">{Math.round(progress)}%</span>
              </div>
            </div>

            <div>
              <p className="font-mystical text-lg font-semibold text-white">{message}</p>
              <p className="mt-1 text-sm text-white/60 animate-pulse">{CYCLING_MESSAGES[msgIndex]}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuroraLoadingOverlay;
